/**
 * entityCrudApi — Frontend client for the unified Entity CRUD endpoint.
 * Used by useHermesActionExecutor to route all Skerpa entity operations.
 */

import type {
  GraphEntity, EntityListFilter, EntityListResult,
  EntityCreateInput, EntityUpdateInput, MutationSource,
  RelationCreateInput, EntityRelation,
} from '@ispot/shared'
import { API_URL, getSessionToken } from './client'

function base(accountId: string, projectId: string) {
  return `/api/accounts/${accountId}/projects/${projectId}/entities`
}

function sourceHeaders(source: MutationSource): Record<string, string> {
  return { 'X-Mutation-Source': source }
}

async function entityFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getSessionToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(`${API_URL}${path}`, { ...options, credentials: 'include', headers })
  const data = await response.json()
  if (!response.ok) throw new Error((data as { error?: string; message?: string }).error || (data as { message?: string }).message || `API error: ${response.status}`)
  return data as T
}

export const entityCrudApi = {
  async listTypes(accountId: string, projectId: string): Promise<string[]> {
    const result = await entityFetch<{ types: string[] }>(`${base(accountId, projectId)}/types`)
    return result.types
  },

  async list(accountId: string, projectId: string, entityType: string, filter?: EntityListFilter): Promise<EntityListResult<GraphEntity>> {
    const params = new URLSearchParams()
    if (filter?.search) params.set('search', filter.search)
    if (filter?.domain) params.set('domain', filter.domain)
    if (filter?.status) params.set('status', filter.status)
    if (filter?.limit) params.set('limit', String(filter.limit))
    if (filter?.offset) params.set('offset', String(filter.offset))
    const qs = params.toString() ? `?${params}` : ''
    return entityFetch<EntityListResult<GraphEntity>>(`${base(accountId, projectId)}/${entityType}${qs}`)
  },

  async get(accountId: string, projectId: string, entityType: string, uri: string): Promise<GraphEntity> {
    const result = await entityFetch<{ entity: GraphEntity }>(`${base(accountId, projectId)}/${entityType}/${encodeURIComponent(uri)}`)
    return result.entity
  },

  async create(accountId: string, projectId: string, data: EntityCreateInput, source: MutationSource = 'user'): Promise<GraphEntity> {
    const result = await entityFetch<{ entity: GraphEntity }>(`${base(accountId, projectId)}/${data.entityType}`, {
      method: 'POST',
      headers: sourceHeaders(source),
      body: JSON.stringify({ label: data.label, description: data.description, confidence: data.confidence }),
    })
    return result.entity
  },

  async update(accountId: string, projectId: string, entityType: string, uri: string, data: EntityUpdateInput, source: MutationSource = 'user'): Promise<GraphEntity> {
    const result = await entityFetch<{ entity: GraphEntity }>(`${base(accountId, projectId)}/${entityType}/${encodeURIComponent(uri)}`, {
      method: 'PATCH',
      headers: sourceHeaders(source),
      body: JSON.stringify(data),
    })
    return result.entity
  },

  async delete(accountId: string, projectId: string, entityType: string, uri: string, source: MutationSource = 'user'): Promise<void> {
    await entityFetch(`${base(accountId, projectId)}/${entityType}/${encodeURIComponent(uri)}`, {
      method: 'DELETE',
      headers: sourceHeaders(source),
    })
  },

  async createRelation(accountId: string, projectId: string, data: RelationCreateInput, source: MutationSource = 'user'): Promise<EntityRelation> {
    const result = await entityFetch<{ relation: EntityRelation }>(`${base(accountId, projectId)}/relations`, {
      method: 'POST',
      headers: sourceHeaders(source),
      body: JSON.stringify(data),
    })
    return result.relation
  },

  async deleteRelation(accountId: string, projectId: string, sourceUri: string, targetUri: string, predicate: string, source: MutationSource = 'user'): Promise<void> {
    await entityFetch(`${base(accountId, projectId)}/relations`, {
      method: 'DELETE',
      headers: sourceHeaders(source),
      body: JSON.stringify({ sourceUri, targetUri, predicate }),
    })
  },
}
